import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleF, GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { EmptyState, Spinner } from '@opendate-app/setlist';

const US_CENTER = { lat: 39.5, lng: -98.35 };
const INITIAL_ZOOM = 4;
const MAP_STYLE = { width: '100%', height: 420, borderRadius: 8 };
const MAP_OPTIONS = { streetViewControl: false, mapTypeControl: false, fullscreenControl: true };
const CIRCLE_OPTIONS = { fillColor: '#1982c4', fillOpacity: 0.35, strokeWeight: 0, clickable: false };
const MIN_RADIUS_PX = 3;
const MAX_RADIUS_PX = 18;

const metersPerPixel = (lat, zoom) => (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;

function DemographicsHeatmap({ points }) {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: window.gon?.google_api_key });
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(INITIAL_ZOOM);

  const maxWeight = useMemo(() => Math.max(1, ...points.map((p) => p.weight)), [points]);

  const handleUnmount = useCallback(() => setMap(null), []);
  const handleZoomChanged = useCallback(() => {
    if (map) setZoom(map.getZoom());
  }, [map]);

  useEffect(() => {
    if (!map || points.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds);
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > 12) map.setZoom(12);
    });
  }, [map, points]);

  if (loadError) return <EmptyState size="sm" title="Map unavailable" description="Google Maps failed to load." />;
  if (!isLoaded) return <div className="d-flex justify-content-center p-5"><Spinner /></div>;
  if (points.length === 0) return <EmptyState size="sm" title="No geocoded orders in this range" />;

  return (
    <GoogleMap
      mapContainerStyle={MAP_STYLE}
      center={US_CENTER}
      zoom={INITIAL_ZOOM}
      options={MAP_OPTIONS}
      onLoad={setMap}
      onUnmount={handleUnmount}
      onZoomChanged={handleZoomChanged}
    >
      {points.map((p) => (
        <CircleF
          key={`${p.lat},${p.lng}`}
          center={{ lat: p.lat, lng: p.lng }}
          radius={(MIN_RADIUS_PX + (MAX_RADIUS_PX - MIN_RADIUS_PX) * Math.sqrt(p.weight / maxWeight)) * metersPerPixel(p.lat, zoom)}
          options={CIRCLE_OPTIONS}
        />
      ))}
    </GoogleMap>
  );
}

export default DemographicsHeatmap;
